import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Pagination,
  Alert,
  CircularProgress
} from '@mui/material';
import axiosInstance from '../utils/axios';

const apiError = (data, fallback) => typeof data?.error === 'string' && data.error ? data.error : fallback;

const DepositAdmin = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devPassword, setDevPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    status: '',
    username: '',
    order_id: '',
    date_from: '',
    date_to: ''
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1
  });
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [updateError, setUpdateError] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    try {
      localStorage.removeItem('dev_password');
    } catch {
      return;
    }
  }, []);

  const statusColors = {
    pending: 'warning',
    paid: 'info',
    confirmed: 'primary',
    crediting: 'warning',
    completed: 'success',
    cancelled: 'error',
    expired: 'default'
  };

  const statusLabels = {
    pending: 'Ожидает',
    paid: 'Оплачен',
    confirmed: 'Подтвержден',
    crediting: 'Зачисление',
    completed: 'Завершен',
    cancelled: 'Отменен',
    expired: 'Истек'
  };

  const fetchOrders = async () => {
    if (!devPassword) {
      setError('Введите пароль разработчика');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const response = await axiosInstance.get(`/deposit-admin/orders?${params}`, {
        headers: { 'x-dev-password': devPassword }
      });

      if (response.data.success === true) {
        setOrders(response.data.data.orders);
        setPagination(response.data.data.pagination);
        setIsAuthenticated(true);
      } else {
        setError(apiError(response.data, 'Ошибка загрузки депозитов'));
      }
    } catch (err) {
      setError(apiError(err.response?.data, 'Ошибка загрузки депозитов'));
      if (err.response?.status === 401 || err.response?.status === 403) {
        setIsAuthenticated(false);
        setDevPassword('');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!devPassword) return;

    try {
      const response = await axiosInstance.get('/deposit-admin/stats', {
        headers: { 'x-dev-password': devPassword }
      });

      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Stats error:', err);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder || !newStatus || savingRef.current || selectedOrder.status === 'completed'
      || newStatus === 'crediting' || (selectedOrder.status === 'crediting' && newStatus !== 'completed')) return;

    savingRef.current = true;
    setSaving(true);
    setUpdateError('');
    try {
      const url = `/deposit-admin/orders/${selectedOrder._id}/status`;
      const payload = { status: newStatus, notes };
      const config = { headers: { 'x-dev-password': devPassword } };
      
      const response = await axiosInstance.patch(url, payload, config);

      if (response.data.success === true) {
        setDialogOpen(false);
        setSelectedOrder(null);
        setNewStatus('');
        setNotes('');
        fetchOrders();
        fetchStats();
      } else {
        setUpdateError(apiError(response.data, 'Ошибка обновления статуса'));
      }
    } catch (err) {
      setUpdateError(apiError(err.response?.data, 'Ошибка обновления статуса'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const openStatusDialog = (order) => {
    if (order.status === 'completed' || savingRef.current) return;
    setUpdateError('');
    setSelectedOrder(order);
    setNewStatus(order.status === 'crediting' ? 'completed' : order.status);
    setNotes(order.notes || '');
    setDialogOpen(true);
  };

  useEffect(() => {
    if (devPassword && isAuthenticated) {
      fetchOrders();
      fetchStats();
    }
  }, [filters.page, filters.status]);

  if (!isAuthenticated) {
    return (
      <Box sx={{ maxWidth: 400, mx: 'auto', mt: 8 }}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Админка депозитов
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Введите пароль разработчика для доступа
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              fullWidth
              type="password"
              label="Пароль разработчика"
              value={devPassword}
              onChange={(e) => setDevPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchOrders()}
              sx={{ mb: 2 }}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={fetchOrders}
              disabled={!devPassword || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Войти'}
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Админка депозитов
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Всего депозитов
                </Typography>
                <Typography variant="h4">{stats.totalOrders}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Сегодня
                </Typography>
                <Typography variant="h4">{stats.todayOrders}</Typography>
              </CardContent>
            </Card>
          </Grid>
          {stats.byStatus.map((stat) => (
            <Grid item xs={12} md={3} key={stat._id}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    {statusLabels[stat._id] || stat._id}
                  </Typography>
                  <Typography variant="h4">{stat.count}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.totalAmount.toFixed(2)} ₽
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel id="deposit-status-filter-label">Статус</InputLabel>
                <Select
                  labelId="deposit-status-filter-label"
                  value={filters.status}
                  label="Статус"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                >
                  <MenuItem value="">Все</MenuItem>
                  <MenuItem value="pending">Ожидает</MenuItem>
                  <MenuItem value="paid">Оплачен</MenuItem>
                  <MenuItem value="confirmed">Подтвержден</MenuItem>
                  <MenuItem value="crediting">Зачисление</MenuItem>
                  <MenuItem value="completed">Завершен</MenuItem>
                  <MenuItem value="cancelled">Отменен</MenuItem>
                  <MenuItem value="expired">Истек</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Username"
                value={filters.username}
                onChange={(e) => setFilters({ ...filters, username: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Order ID"
                value={filters.order_id}
                onChange={(e) => setFilters({ ...filters, order_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="От"
                InputLabelProps={{ shrink: true }}
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="До"
                InputLabelProps={{ shrink: true }}
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={fetchOrders}
                disabled={loading}
                sx={{ height: '56px' }}
              >
                Поиск
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order ID</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Сумма</TableCell>
              <TableCell>Метод</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Создан</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Нет депозитов
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order._id}>
                  <TableCell>{order.order_id}</TableCell>
                  <TableCell>{order.username}</TableCell>
                  <TableCell>
                    {order.amount} ₽ / {order.total_amount} ₽
                  </TableCell>
                  <TableCell>{order.method}</TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabels[order.status] || order.status}
                      color={statusColors[order.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(order.createdAt).toLocaleString('ru-RU')}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openStatusDialog(order)}
                      disabled={order.status === 'completed' || saving}
                    >
                      Изменить
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Pagination
          count={pagination.pages}
          page={pagination.page}
          onChange={(e, page) => setFilters({ ...filters, page })}
          color="primary"
        />
      </Box>

      {/* Status Update Dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} aria-labelledby="deposit-status-dialog-title" maxWidth="sm" fullWidth>
        <DialogTitle id="deposit-status-dialog-title">Изменить статус депозита</DialogTitle>
        <DialogContent>
          {updateError && <Alert severity="error" sx={{ mt: 2 }}>{updateError}</Alert>}
          {selectedOrder && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Order ID: {selectedOrder.order_id}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Username: {selectedOrder.username}
              </Typography>
              <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                Сумма: {selectedOrder.amount} ₽
              </Typography>
              
              {selectedOrder.status === 'crediting' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Зачисление начато, но завершение ещё не подтверждено. Проверьте данные и повторите завершение. Повторное зачисление защищено журналом.
                </Alert>
              )}
              <FormControl fullWidth sx={{ mb: 2 }} disabled={saving}>
                <InputLabel id="deposit-new-status-label">Новый статус</InputLabel>
                <Select
                  labelId="deposit-new-status-label"
                  value={newStatus}
                  label="Новый статус"
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  {(selectedOrder.status === 'crediting'
                    ? ['completed']
                    : ['pending', 'paid', 'confirmed', 'completed', 'cancelled', 'expired']
                  ).map(status => <MenuItem key={status} value={status}>{statusLabels[status]}</MenuItem>)}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Примечания"
                disabled={saving}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Отмена</Button>
          <Button onClick={handleUpdateStatus} variant="contained" disabled={saving || !newStatus}>
            {saving ? 'Сохранение...' : selectedOrder?.status === 'crediting' ? 'Повторить завершение' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DepositAdmin;
